import type React from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import testimonialpatterns from "../../../../../../../../assets/testimonialspattern.png";
import muzukuruLogo from "../../../../../../../../assets/logo/new-muzukuru-logo.png";
import apiService from "../../../../../../../../service/Api";
import { Proposal } from "../../../../../types/proposal.types";

interface Testimonial {
  id: number;
  author_name: string;
  author_description: string;
  company_name: string | null;
  rating: number;
  content: string;
  display_order: number;
}

interface TestimonialsPageProps {
  proposal: Proposal;
}

const TestimonialsPage: React.FC<TestimonialsPageProps> = ({ proposal }) => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await apiService.get<Testimonial[]>(
          "/api/configurations/testimonials/",
        );

        let fetchedTestimonials = response.data || [];

        const selectedIds = proposal.testimonials || [];

        if (selectedIds.length > 0) {
          fetchedTestimonials = fetchedTestimonials.filter((t) =>
            selectedIds.includes(t.id),
          );
        } else {
          fetchedTestimonials = fetchedTestimonials.slice(0, 3);
        }

        setTestimonials(fetchedTestimonials);
      } catch (error) {
        console.error("Failed to fetch testimonials:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, [proposal.testimonials]);

  return (
    <Box
      id="testimonial-page"
      sx={{
        width: "816px",
        height: "1056px",
        backgroundImage: `url(${testimonialpatterns})`,
        backgroundRepeat: "repeat",
        backgroundSize: "auto",
        py: 10,
        px: 8,
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      {/* Title */}
      <Typography
        variant="h1"
        align="center"
        sx={{
          letterSpacing: 4,
          color: "#7a7a6f",
          mb: 6,
          fontWeight: 800,
        }}
      >
        TESTIMONIALS
      </Typography>

      {/* Testimonial Cards Wrapper */}
      <Box sx={{ flex: 1 }}>
        {loading ? (
          <Box sx={{ textAlign: "center", mt: 8 }}>
            <CircularProgress size={32} sx={{ color: "#7a7a6f" }} />
          </Box>
        ) : testimonials.length === 0 ? (
          <Typography variant="body1" sx={{ textAlign: "center", mt: 6 }}>
            No testimonials selected.
          </Typography>
        ) : (
          testimonials.map((item, index) => (
            <Box
              key={index}
              sx={{
                backgroundColor: "#faf9f6",
                borderRadius: 4,
                px: 6,
                py: 3,
                mb: 3,
                boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
                textAlign: "center",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  lineHeight: 1.8,
                  mb: 2,
                  color: "#2e2e2e",
                  fontSize: "14px",
                }}
              >
                “{item.content}”
              </Typography>

              {/* Signature */}
              <Typography
                sx={{
                  fontFamily: "Brittany, cursive, serif",
                  fontSize: 24,
                  color: "#2e2e2e",
                }}
              >
                “{item.author_name}”
              </Typography>
            </Box>
          ))
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ textAlign: "center", mt: "auto", pt: 4 }}>
        <img
          src={muzukuruLogo}
          alt="Muzukuru logo"
          style={{ width: 160, height: "auto", marginBottom: 12 }}
        />
        <Typography variant="caption" display="block" sx={{ color: "#7a7a6f" }}>
          Phone: +263 776 402 402 | tawanda@muzukuru.com | www.muzukuru.co.zw
        </Typography>
        <Typography variant="caption" display="block" sx={{ color: "#7a7a6f" }}>
          46B Mount Pleasant Drive, Mount Pleasant, Harare, Zimbabwe
        </Typography>
      </Box>
    </Box>
  );
};

export default TestimonialsPage;
